import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Input component props extending MUI TextField props
 */
interface InputComponentProps extends Omit<TextFieldProps, 'variant'> {
  isEntry?: boolean;
  isNav?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  readOnly?: boolean;
}

/**
 * Styled TextField component with Figma design specifications
 * 
 * Design specs from Figma:
 * - Border radius: 4px
 * - Border: 0.6px solid rgba(17, 17, 17, 0.15)
 * - Background: #FFF (light mode) / theme background
 * - Box shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.05)
 */
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => !['isEntry', 'isNav'].includes(prop as string),
})<InputComponentProps>(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.05)',
    fontSize: '12px',
    fontFamily: '"Open Sans"',
    fontWeight: 400,
    
    '& fieldset': {
      borderWidth: '0.6px',
      borderStyle: 'solid',
      borderColor: theme.palette.mode === 'light' 
        ? 'rgba(17, 17, 17, 0.15)' 
        : theme.palette.divider,
      borderRadius: '4px',
      transition: 'border-color 0.2s',
    },
    
    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'light' 
        ? 'rgba(17, 17, 17, 0.25)' 
        : theme.palette.action.hover,
    },
    
    '&.Mui-focused fieldset': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
    },
    
    '&.Mui-error fieldset': {
      borderWidth: '1px',
      borderColor: theme.palette.error.main,
    },
    
    '&.Mui-disabled': {
      backgroundColor: theme.palette.mode === 'light'
        ? '#F5F5F5'
        : theme.palette.action.disabledBackground,
      '& fieldset': {
        borderColor: theme.palette.mode === 'light'
          ? 'rgba(17, 17, 17, 0.15)'
          : theme.palette.divider,
      },
    },
    
    '&.Mui-readOnly': {
      backgroundColor: theme.palette.action.disabledBackground,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
    },
    
    '& input': {
      padding: '8px 12px',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '130%',
      letterSpacing: '-0.24px',
      fontFamily: '"Open Sans"',
      fontStyle: 'normal',
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontFeatureSettings: "'liga' off, 'clig' off",
      
      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 0.7,
      },
      
      '&:disabled': {
        color: theme.palette.mode === 'light'
          ? 'rgba(17, 17, 17, 0.40)'
          : theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.mode === 'light'
          ? 'rgba(17, 17, 17, 0.40)'
          : theme.palette.text.disabled,
      },
    },
    
    // Handle input adornments
    '& .MuiInputAdornment-root': {
      marginRight: 0,
      '& svg': {
        fontSize: '20px',
        color: theme.palette.text.secondary,
      },
    },
  },
  
  // Label styling
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    fontFamily: '"Open Sans"',
    fontWeight: 400,
    lineHeight: '130%',
    letterSpacing: '-0.24px',
    
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    
    '&.Mui-error': {
      color: theme.palette.error.main,
    },
    
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  },
  
  // Helper text styling
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
    marginTop: '4px',
    marginLeft: '12px',
    
    '&.Mui-error': {
      color: theme.palette.error.main,
    },
  },
}));

/**
 * Input component with Material UI and Figma design specifications
 * 
 * This component provides a consistent input field that matches
 * the Figma design system while adapting to light/dark themes.
 * 
 * @example
 * ```tsx
 * <Input
 *   fullWidth
 *   placeholder="Enter text"
 *   value={value}
 *   onChange={handleChange}
 * />
 * ```
 */
const Input: React.FC<InputComponentProps> = (props) => {
  const {
    variant = 'outlined',
    size = 'small',
    readOnly,
    InputProps,
    isEntry,
    isNav,
    ...otherProps
  } = props;

  // Merge readOnly into InputProps if needed
  const inputProps = {
    ...InputProps,
    ...(readOnly && { readOnly: true }),
  };

  return (
    <StyledTextField
      variant={variant}
      size={size}
      InputProps={inputProps}
      {...otherProps}
    />
  );
};

export default Input;